import mixpanel from 'mixpanel-browser';

import { getRouteMeta } from '../routeMetadata';
import {
  getAnalyticsPath,
  getProductAnalyticsContext,
  type ProductAnalyticsContext,
} from './productAnalytics';

type AnalyticsProperties = Record<string, boolean | number | string>;

interface AnalyticsUser {
  carti_premium?: {
    premium_status?: boolean;
  };
  user_id?: string;
}

const projectToken = process.env.REACT_APP_MIXPANEL_TOKEN;
let isInitialized = false;
let lastPageView = { pagePath: '', trackedAt: 0 };
let lastProductOpen = { pagePath: '', trackedAt: 0 };
let currentRouteProperties: AnalyticsProperties = {};

const DUPLICATE_EVENT_WINDOW_MILLISECONDS = 250;
const environment = process.env.NODE_ENV ?? 'unknown';

export const initializeAnalytics = () => {
  if (isInitialized || !projectToken) return;

  mixpanel.init(projectToken, {
    autocapture: {
      block_attrs: ['aria-describedby', 'aria-label', 'aria-labelledby', 'href', 'name', 'title'],
      capture_text_content: false,
      click: true,
      dead_click: true,
      input: true,
      pageview: false,
      rage_click: true,
      scroll: true,
      submit: true,
    },
    debug: process.env.NODE_ENV !== 'production',
    persistence: 'localStorage',
    property_blacklist: [
      '$current_url',
      '$current_url_domain',
      '$current_url_hash',
      '$current_url_host',
      '$current_url_path',
      '$current_url_protocol',
      '$current_url_search',
      '$initial_referrer',
      '$host',
      '$pathname',
      '$referrer',
      '$el_attr__href',
    ],
    save_referrer: false,
    skip_first_touch_marketing: true,
    stop_utm_persistence: true,
    store_google: false,
    track_pageview: false,
  });
  mixpanel.register({
    authentication_state: 'anonymous',
    environment,
    platform: 'web',
  });
  isInitialized = true;
};

const canTrack = () => {
  initializeAnalytics();
  return isInitialized;
};

const trackEvent = (eventName: string, properties: AnalyticsProperties) => {
  if (!canTrack()) return false;
  mixpanel.track(eventName, properties);
  return true;
};

const productProperties = (context: ProductAnalyticsContext): AnalyticsProperties => ({
  page_path: context.page_path,
  product_category: context.product_category,
  product_name: context.product_name,
});

export const trackPageView = (pathname: string) => {
  if (!canTrack()) return;

  const pagePath = getAnalyticsPath(pathname);
  const trackedAt = performance.now();
  if (
    lastPageView.pagePath === pagePath &&
    trackedAt - lastPageView.trackedAt < DUPLICATE_EVENT_WINDOW_MILLISECONDS
  ) {
    return;
  }
  lastPageView = { pagePath, trackedAt };
  const product = getProductAnalyticsContext(pathname);
  const properties: AnalyticsProperties = {
    page_path: pagePath,
    page_title: getRouteMeta(pathname)?.title ?? document.title,
  };

  currentRouteProperties = product
    ? { ...properties, ...productProperties(product) }
    : properties;
  if (!product) {
    mixpanel.unregister('product_category');
    mixpanel.unregister('product_name');
  }
  mixpanel.register(currentRouteProperties);

  mixpanel.track('$mp_web_page_view', currentRouteProperties);
};

export const startProductSession = (pathname: string) => {
  const product = getProductAnalyticsContext(pathname);
  if (!product || !canTrack()) {
    return () => undefined;
  }

  const trackedAt = performance.now();
  if (
    lastProductOpen.pagePath !== product.page_path ||
    trackedAt - lastProductOpen.trackedAt >= DUPLICATE_EVENT_WINDOW_MILLISECONDS
  ) {
    trackEvent('product_opened', productProperties(product));
    lastProductOpen = { pagePath: product.page_path, trackedAt };
  }

  let activeMilliseconds = 0;
  let activeSince = document.visibilityState === 'visible' ? performance.now() : null;
  let isEnded = false;

  const pauseTimer = () => {
    if (activeSince === null) return;
    activeMilliseconds += performance.now() - activeSince;
    activeSince = null;
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      activeSince = performance.now();
    } else {
      pauseTimer();
    }
  };

  const endSession = () => {
    if (isEnded) return;
    isEnded = true;
    pauseTimer();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', endSession);
    if (activeMilliseconds < DUPLICATE_EVENT_WINDOW_MILLISECONDS) return;
    trackEvent('product_session_ended', {
      ...productProperties(product),
      duration_seconds: Number((activeMilliseconds / 1000).toFixed(2)),
    });
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', endSession);
  return endSession;
};

export const identifyUser = (user: AnalyticsUser) => {
  if (!user.user_id || !canTrack()) return;

  const planType = user.carti_premium?.premium_status ? 'premium' : 'free';
  mixpanel.identify(user.user_id);
  mixpanel.people.set({ plan_type: planType });
  mixpanel.register({ authentication_state: 'authenticated', plan_type: planType });
};

export const trackSignUp = (user: AnalyticsUser, signUpMethod: 'email' | 'google') => {
  if (!user.user_id) return;
  identifyUser(user);
  trackEvent('sign_up_completed', {
    platform: 'web',
    sign_up_method: signUpMethod,
  });
};

export const resetAnalytics = () => {
  if (!canTrack()) return;
  mixpanel.reset();
  mixpanel.register({
    authentication_state: 'anonymous',
    ...currentRouteProperties,
    environment,
    platform: 'web',
  });
};

export const trackCartineseTranslation = ({
  inputLength,
  inputSource,
  isPremium,
  outputCount,
  remixAction,
  styleName,
}: {
  inputLength: number;
  inputSource: 'custom' | 'sample';
  isPremium: boolean;
  outputCount?: number;
  remixAction?: string;
  styleName?: string;
}) => {
  const properties: AnalyticsProperties = {
    input_length: inputLength,
    input_source: inputSource,
    is_premium: isPremium,
    output_count: outputCount ?? 1,
  };
  if (remixAction) properties.remix_action = remixAction;
  if (styleName) properties.style_name = styleName;
  trackEvent('cartinese_translation_completed', properties);
};

export const trackAgentTurn = ({
  codeRunCount,
  proposedAction,
  turnType,
}: {
  codeRunCount: number;
  proposedAction: boolean;
  turnType: 'message' | 'approval' | 'rejection';
}) => {
  trackEvent('agent_turn_completed', {
    code_run_count: codeRunCount,
    proposed_action: proposedAction,
    turn_type: turnType,
  });
};
